/**
 * OpenTelemetry Web SDK bootstrap for browser applications (Vue 3).
 *
 * Provides a single entry point — `initializeTelemetryBrowser(config)` — that configures
 * the Web Tracer Provider with OTLP HTTP exporters and W3C trace propagation.
 *
 * When `OTEL_ENABLED` is absent or set to `"false"` the function returns a no-op
 * object immediately, incurring zero CPU or memory overhead.
 *
 * Usage:
 * ```ts
 * // Must be called BEFORE any other module that creates spans or makes HTTP requests
 * const otel = initializeTelemetryBrowser({ serviceName: 'frontend', ... })
 *
 * // On app unmount or page unload:
 * await otel.shutdown()
 * ```
 */
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { propagation } from '@opentelemetry/api'
import { W3CTraceContextPropagator } from '@opentelemetry/core'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import type { TelemetryConfig } from './types'
import { getSamplingRate } from './config'
import { withTimeout } from './utils'

export interface TelemetryBrowserInstance {
    /** Graceful shutdown — flushes pending spans. */
    shutdown: () => Promise<void>
    /** Force flush pending spans immediately. */
    flush: () => Promise<void>
}

let lifecycleListenersRegistered = false

function registerLifecycleListeners(flush: () => Promise<void>): void {
    if (lifecycleListenersRegistered) {
        return
    }

    lifecycleListenersRegistered = true

    const flushBestEffort = (): void => {
        void withTimeout(flush(), 2_000, 'browser flush')
    }

    window.addEventListener('pagehide', flushBestEffort)
    window.addEventListener('beforeunload', flushBestEffort)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushBestEffort()
        }
    })
}

/**
 * Reads OTel environment variables and returns a parsed config object.
 * Returns `null` when telemetry is explicitly disabled.
 */
function parseBrowserConfig(
    override?: Partial<TelemetryConfig>,
): TelemetryConfig | null {
    // Explicit opt-out: callers gate on VITE_OTEL_ENABLED and pass the result
    // here. Browser bundles cannot read process.env.OTEL_ENABLED themselves.
    if (override?.enabled === false) {
        return null
    }
    const serviceName = override?.serviceName ?? 'frontend'
    const environment = override?.environment ?? 'development'
    const exporterEndpoint =
        override?.exporterEndpoint ?? 'http://localhost:4318'
    const samplingRate = getSamplingRate(override?.environment ?? 'development')

    return {
        serviceName,
        environment,
        exporterEndpoint,
        samplingRate,
        otlpHeaders: override?.otlpHeaders ?? {},
        signozApiKey: override?.signozApiKey ?? '',
        logLevel: override?.logLevel ?? 'info',
        propagateCorsUrls: override?.propagateCorsUrls,
    }
}

/**
 * Initializes the OpenTelemetry Web SDK with OTLP HTTP exporter and
 * W3C trace propagation for distributed tracing.
 *
 * Returns a no-op object if `OTEL_ENABLED` is not `"true"`.
 */
export function initializeTelemetryBrowser(
    override?: Partial<TelemetryConfig>,
): TelemetryBrowserInstance {
    const config = parseBrowserConfig(override)
    if (!config) {
        return { shutdown: async () => {}, flush: async () => {} }
    }

    const resource = resourceFromAttributes({
        [ATTR_SERVICE_NAME]: config.serviceName,
        'deployment.environment': config.environment,
        'service.version': '0.0.0',
    })

    const traceExporter = new OTLPTraceExporter({
        url: `${config.exporterEndpoint}/v1/traces`,
        headers: config.otlpHeaders,
    })

    const provider = new WebTracerProvider({
        resource,
        spanProcessors: [new BatchSpanProcessor(traceExporter)],
    })

    provider.register()
    // Set W3C trace propagator globally for distributed tracing
    propagation.setGlobalPropagator(new W3CTraceContextPropagator())

    // Register browser instrumentations to automatically capture spans.
    // Propagate traceparent only to the configured backend origin — injecting
    // it into third-party cross-origin fetches breaks their CORS preflight.
    new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: config.propagateCorsUrls
            ? [config.propagateCorsUrls]
            : [],
    }).enable()
    new DocumentLoadInstrumentation().enable()
    registerLifecycleListeners(() => provider.forceFlush())

    return {
        shutdown: async () => {
            try {
                await withTimeout(
                    provider.shutdown(),
                    10_000,
                    'browser shutdown',
                )
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(
                    '[telemetry-browser] Error during shutdown',
                    error,
                )
            }
        },
        flush: async () => {
            try {
                await withTimeout(
                    provider.forceFlush(),
                    10_000,
                    'browser flush',
                )
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('[telemetry-browser] Error during flush', error)
            }
        },
    }
}
